import { cn } from "@/lib/utils";
import { formatShortDate } from "@/lib/campaignPlanning";
import { PHASES, STEP_COPY, LOCKED_LABEL, displayStatus } from "@/components/schools/setup/steps";
import { StepStatusIcon } from "@/components/schools/setup/SetupPrimitives";

/**
 * The setup guide's step list, grouped by phase. Each row shows where
 * the step stands, whether it's optional, and when to aim for.
 */
export default function SetupStepper({ steps, activeKey, onSelect }) {
  return (
    <nav aria-label="Setup steps" className="space-y-5">
      {PHASES.map((phase) => {
        const phaseSteps = steps.filter((s) => s.phase === phase.key);
        if (phaseSteps.length === 0) return null;
        const complete = phaseSteps.filter((s) => s.status === "done" || s.status === "skipped").length;

        return (
          <div key={phase.key}>
            <div className="mb-1.5 flex items-baseline justify-between px-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{phase.title}</p>
              {phase.key !== "launch" && (
                <p className="text-[10px] text-muted-foreground font-tabular">{complete}/{phaseSteps.length}</p>
              )}
            </div>
            <ol className="space-y-0.5">
              {phaseSteps.map((step) => (
                <li key={step.key}>
                  <StepperRow
                    step={step}
                    number={steps.indexOf(step) + 1}
                    active={step.key === activeKey}
                    onSelect={() => onSelect(step.key)}
                  />
                </li>
              ))}
            </ol>
          </div>
        );
      })}
    </nav>
  );
}

function StepperRow({ step, number, active, onSelect }) {
  const copy = STEP_COPY[step.key];
  const complete = step.status === "done" || step.status === "skipped";

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? "step" : undefined}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active ? "bg-primary/10" : "hover:bg-secondary/60"
      )}
    >
      <StepStatusIcon status={displayStatus(step)} number={number} size="sm" />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block truncate text-sm font-medium",
            active ? "text-primary" : complete ? "text-muted-foreground" : "text-foreground"
          )}
        >
          {copy.short}
        </span>
        <StepperMeta step={step} />
      </span>
    </button>
  );
}

function StepperMeta({ step }) {
  // The status icon already announces "Skipped" and "Locked" to screen readers.
  if (step.status === "skipped") return <span aria-hidden="true" className="block text-[11px] text-muted-foreground">Skipped</span>;
  if (step.status === "done") return null;
  if (displayStatus(step) === "locked") return <span aria-hidden="true" className="block text-[11px] text-warning-foreground">{LOCKED_LABEL}</span>;

  const parts = [];
  if (step.tier === "optional") parts.push("Optional");
  if (step.tier === "required" && step.key !== "launch") parts.push("Required");
  if (step.due_on) parts.push(step.overdue ? "ASAP" : `By ${formatShortDate(step.due_on)}`);
  if (parts.length === 0) return null;

  return (
    <span className={cn("block text-[11px]", step.overdue ? "text-warning-foreground" : "text-muted-foreground")}>
      {parts.join(" · ")}
    </span>
  );
}
